import React from "react";
import { CircularProgress, withStyles } from "@material-ui/core";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import PropTypes from "prop-types";
import Downshift from "downshift";
import MuiTextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import uuid from "react-uuid";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import cssStyles from "./multiAutocomplete.component.module.css";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    height: 250,
    width: "103%",
    margin: 0,
  },
  container: {
    flexGrow: 1,
    position: "relative",
    width: "100%",
  },
  paper: {
    position: "absolute",
    zIndex: 12,
    marginTop: theme.spacing(),
    left: 0,
    right: 0,
    maxHeight: 150,
    overflowY: "auto",
  },
  chip: {
    margin: "3px",
    width: "fit-content",
    backgroundColor: "#155DFC",
    borderRadius: "8px",
    color: "#fff",
    ">svg": {
      color: "#fff",
    },
  },
  inputRoot: {
    flexWrap: "wrap",
    //margin: 0,
  },
  inputInput: {
    flexGrow: 1,
  },
  divider: {
    height: theme.spacing(2),
  },
});

function renderInput(inputProps) {
  const { InputProps, classes, ref, width, placeholder, ...other } = inputProps;

  return (
    <MuiThemeProvider theme={theme}>
      {
        <TextField
          style={{ width }}
          variant="outlined"
          className="inputText"
          InputProps={{
            inputRef: ref,
            classes: {
              root: classes.inputRoot,
              input: classes.inputInput,
            },
            ...InputProps,
          }}
          {...other}
        />
      }
    </MuiThemeProvider>
  );
}

function renderSuggestion({
  suggestion,
  index,
  itemProps,
  highlightedIndex,
  selectedItem,
}) {
  const isHighlighted = highlightedIndex === index;

  return (
    <MenuItem
      {...itemProps}
      key={uuid()}
      selected={isHighlighted}
      component="div"
    >
      {suggestion.label}
    </MenuItem>
  );
}

renderSuggestion.propTypes = {
  highlightedIndex: PropTypes.number,
  index: PropTypes.number,
  itemProps: PropTypes.object,
  selectedItem: PropTypes.string,
  suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

function MultiAutocompleteComponent({
  inputValue,
  handleChange,
  selectedItem,
  classes,
  handleInputChange,
  handleKeyDown,
  getSuggestions,
  handleDelete,
  placeholder,
  width,
  disabled = false,
  loading,
}) {
  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          textAlign: "center",
          border: "none",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#F3F3F5",
          height: "36px",
        }}
      >
        <CircularProgress
          style={{ height: "15px", width: "15px", color: "black" }}
        />
      </div>
    );
  } else {
    return (
      <Downshift
        id="downshift-multiple"
        inputValue={inputValue}
        onChange={handleChange}
        selectedItem={selectedItem}
      >
        {({
          getInputProps,
          getItemProps,
          isOpen,
          inputValue: inputValue2,
          selectedItem: selectedItem2,
          highlightedIndex,
        }) => (
          <div className={classes.container}>
            {renderInput({
              fullWidth: true,
              disabled: disabled || false,
              classes,
              loading,
              InputProps: getInputProps({
                startAdornment: (
                  <div
                    /*  className="row"
                    style={{
                      width: "100%",
                      paddingLeft: "14px",
                      paddingTop: "2px",
                    }} */
                    className={cssStyles.chipContainer}
                    key={uuid()}
                  >
                    {selectedItem?.map((item) => (
                      <div key={uuid()}>
                        <MuiThemeProvider theme={theme}>
                          <Chip
                            className={classes.chip}
                            color="primary"
                            key={uuid()}
                            tabIndex={-1}
                            label={item.label}
                            deleteIcon={<CloseOutlinedIcon />}
                            onDelete={handleDelete(item.value)}
                          />
                        </MuiThemeProvider>
                      </div>
                    ))}
                  </div>
                ),
                onChange: handleInputChange,
                onKeyDown: handleKeyDown,
                placeholder,
                width,
              }),
              width,
            })}
            {isOpen ? (
              <Paper className={classes.paper} square>
                {getSuggestions(inputValue2)?.filter((suggestion) => {
                  // FIX 1: Use 'selectedItem' (the prop) instead of 'selectedItem2' (Downshift state)
                  // FIX 2: Handle Object comparison for fullValue={true}
                  return !selectedItem?.some((selected) => {
                    const selectedValue = selected.value;
                    const suggestionValue = suggestion.value;

                    // If both are objects (fullValue mode), compare their _id
                    if (
                      typeof selectedValue === "object" &&
                      selectedValue !== null &&
                      typeof suggestionValue === "object" &&
                      suggestionValue !== null
                    ) {
                      return selectedValue._id === suggestionValue._id;
                    }

                    // Otherwise compare normally (primitive values)
                    return selectedValue === suggestionValue;
                  });
                })
                  ?.map((suggestion, index) =>
                    renderSuggestion({
                      suggestion,
                      index,
                      itemProps: getItemProps({ item: suggestion }),
                      highlightedIndex,
                      selectedItem: selectedItem2,
                    })
                  )}
              </Paper>
            ) : null}
          </div>
        )}
      </Downshift>
    );
  }
}

MultiAutocompleteComponent.propTypes = {
  inputValue: PropTypes.any.isRequired,
  getSuggestions: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  selectedItem: PropTypes.array.isRequired,
  width: PropTypes.string,
  disabled: PropTypes.bool,
};

const theme = createTheme({
  typography: {
    useNextVariants: true,
  },
  overrides: {
    MuiChip: {
      root: {
        color: "#fff",
        backgroundColor: "rgb(35, 47, 62)",
        borderRadius: "2px",
        fontSize: "12px",
        height: "25px",
      },
      deleteIcon: {
        color: "white !important",
        width: "15px",
      },
    },
    MuiOutlinedInput: {
      root: {
        minHeight: 36,
        borderRadius: "8px",
      },
      notchedOutline: {
        border: "none",
      },
      // "$&focused": {
      //   border: "2px solid #122526",
      //   borderColor: "#122526",
      // },
      inputAdornedStart: {
        paddingLeft: "4px !important",
      },
      adornedStart: {
        padding: "6px !important",
      },
      adornedEnd: {
        padding: "0 !important",
      },
      input: {
        padding: 10,
      },
    },
    MuiTextField: {
      root: {
        minHeight: 35,
        margin: "0px",
      },
    },
    MuiInputBase: {
      root: {
        fontFamily: "Montserrat",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: 12,
        color: "#717182",
        backgroundColor: "#F3F3F5",
      },
    },
    MuiInput: {
      root: {
        border: "none",
      },
    },
    MuiPaper: {
      root: {
        zIndex: 1200,
      },
    },
  },
});

const TextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "green",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "green",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "red",
      },
      "&:hover fieldset": {
        borderColor: "yellow",
      },
      "&.Mui-focused fieldset": {
        borderColor: "green",
      },
    },
  },
})(MuiTextField);

export default withStyles(styles)(MultiAutocompleteComponent);
